import { useMemo } from "react";
import { DataTable, type Column } from "@/components/DataTable";
import type { Indicador } from "@/components/Indicadores";
import { RelatorioLayout } from "@/components/escala/RelatorioLayout";
import { FiltroRelatorio } from "@/components/escala/RelatorioFiltroCampos";
import { porNome, useRelatorio } from "@/components/escala/useRelatorio";
import { distintos, formatNumero, plural } from "@/components/escala/relatorio";
import { relValeTransporte, rotuloLoja, type LinhaVT } from "@/api/escala.service";

const CONTAGENS = ["trabalhados", "folgas", "compensacao", "folga_feriado", "afastados", "desligados"] as const;

type Totais = Record<(typeof CONTAGENS)[number], number>;

function somar(linhas: LinhaVT[]): Totais {
  const totais = Object.fromEntries(CONTAGENS.map((k) => [k, 0])) as Totais;
  for (const l of linhas) for (const k of CONTAGENS) totais[k] += l[k];
  return totais;
}

function indicadoresVT(linhas: LinhaVT[], totais: Totais): [Indicador, Indicador, Indicador, Indicador] {
  const afastados = linhas.filter((l) => l.afastados > 0).length;
  const desligados = linhas.filter((l) => l.desligados > 0).length;
  return [
    { titulo: "Colaboradores", valor: linhas.length, nota: `em ${plural(distintos(linhas, (l) => l.loja_id), "loja")}` },
    {
      titulo: "Dias trabalhados",
      valor: totais.trabalhados.toLocaleString("pt-BR"),
      nota: linhas.length ? `média de ${formatNumero(totais.trabalhados / linhas.length)} por colaborador` : "nenhum colaborador no filtro",
    },
    {
      titulo: "Folgas e compensações",
      valor: (totais.folgas + totais.folga_feriado + totais.compensacao).toLocaleString("pt-BR"),
      nota: `${plural(totais.folgas, "folga")} · ${totais.folga_feriado} de feriado · ${plural(totais.compensacao, "compensação", "compensações")}`,
    },
    {
      titulo: "Afastados e desligados",
      valor: linhas.filter((l) => l.afastados > 0 || l.desligados > 0).length,
      nota: `${plural(afastados, "afastado")} · ${plural(desligados, "desligado")}`,
    },
  ];
}

// Dias distintos por tipo de trabalho, por colaborador; só escalas aprovadas entram.
export function EscalaRelValeTransporteTab() {
  const r = useRelatorio({ buscar: relValeTransporte, ordenar: porNome, mensagemErro: "Erro ao carregar o relatório de vale-transporte" });

  const totais = useMemo(() => somar(r.linhas), [r.linhas]);
  const indicadores = useMemo(() => indicadoresVT(r.linhas, totais), [r.linhas, totais]);

  const columns: Column<LinhaVT>[] = [
    { key: "matricula", label: "Matrícula" },
    { key: "nome", label: "Nome" },
    { key: "cargo", label: "Cargo" },
    { key: "loja_id", label: "Loja", render: (l) => rotuloLoja(r.lojas, l.loja_id), exportValue: (l) => rotuloLoja(r.lojas, l.loja_id) },
    { key: "trabalhados", label: "Trabalhados" },
    { key: "folgas", label: "Folgas" },
    { key: "compensacao", label: "Compensação" },
    { key: "folga_feriado", label: "Folga de Feriado" },
    { key: "afastados", label: "Afastados" },
    { key: "desligados", label: "Desligados" },
  ];

  return (
    <RelatorioLayout
      carregando={r.carregando}
      filtro={<FiltroRelatorio estado={r} />}
      indicadores={indicadores}
      exportacao={{ colunas: columns, linhas: r.ordenadas, arquivo: "vale-transporte", aba: "Vale Transporte" }}
      competencia={r.filtros}
    >
      <DataTable
        columns={columns}
        {...r.tabela}
        footer={{ matricula: "Total", nome: plural(r.linhas.length, "colaborador", "colaboradores"), ...totais }}
      />
    </RelatorioLayout>
  );
}
